import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MessengerLayout        from "../../layout/MessengerLayout";
import AdminFilters           from "./filters/AdminFilters";
import OrgComposition         from "./widgets/OrgComposition";
import AttendanceLeave        from "./widgets/AttendanceLeave";
import PayrollSummary         from "./widgets/PayrollSummary";
import PerformanceRadar       from "./widgets/PerformanceRadar";
import TicketsStatus          from "./widgets/TicketsStatus";
import CompliancePanel        from "./widgets/CompliancePanel";
import ApprovalsHub           from "./widgets/ApprovalsHub";
import AnnouncementsCaster    from "./widgets/AnnouncementsCaster";
import DemoResetButton        from "../demo/DemoResetButton";
import ErrorBoundary          from "../ui/ErrorBoundary";
import { ScrollText }         from "lucide-react";
import { isTenantCaseAdmin, useErModuleOn } from "./cases/AdminCasesRoute";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  // ER CASES CARD — TWO CONDITIONS, BOTH READ FROM THE SAME PLACE THE
  // ROUTE READS THEM.
  //
  // isTenantCaseAdmin() is the predicate AdminCasesRoute uses and mirrors
  // the backend's own gate: the union of `role` and the `roles` CSV, admin
  // only, superadmin refused FIRST and separately. It is imported rather
  // than retyped because a card that appears for somebody the route then
  // refuses is worse than no card — and the local `isAdmin` above is not
  // the same test (it admits superadmin through user.role).
  //
  // useErModuleOn() hides the card for an organisation that has not bought
  // the module, reading `modules.er_cases` directly rather than through
  // isEnabled(), which is fail-open by design. Same reasoning as
  // Sidebar.useShowCasesEntry.
  const showCases = isTenantCaseAdmin();
  const erModuleOn = useErModuleOn();
  // Role gate
  let user = null;
  try { user = JSON.parse(localStorage.getItem("user") || "null"); } catch {}
  const isAdmin = !!(user && (user.is_admin || user.role === "admin" || user.roles?.includes?.("admin")));

  const [filters, setFilters] = useState({
    from:       new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10),
    to:         new Date().toISOString().slice(0, 10),
    location:   "ALL",
    department: "ALL",
    empType:    "ALL",
    payGroup:   "ALL",
  });

  const [kpis,    setKpis]    = useState(null);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;

    const loadDashboardData = () => {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        from:       filters.from,
        to:         filters.to,
        ...(filters.location   !== "ALL" && { location:   filters.location   }),
        ...(filters.department !== "ALL" && { department: filters.department }),
        ...(filters.empType    !== "ALL" && { emp_type:   filters.empType    }),
        ...(filters.payGroup   !== "ALL" && { pay_group:  filters.payGroup   }),
      });

      Promise.all([
        fetch(`${API_BASE}/api/admin/dashboard/kpis?${params}`,    { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/dashboard/widgets?${params}`, { headers: authHeaders() }).then(r => r.json()),
      ])
        .then(([kpiRes, widgetRes]) => {
          if (!active) return;
          setKpis(kpiRes);
          setData(widgetRes);
        })
        .catch(e => { if (active) setError(e.message); })
        .finally(() => { if (active) setLoading(false); });
    };

    loadDashboardData();
    // Admin dashboard refreshes on every K.I.R.A action.
    const handleKiraAction = () => loadDashboardData();
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      active = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [isAdmin, filters.from, filters.to, filters.location, filters.department, filters.empType, filters.payGroup]);

  if (!isAdmin) {
    return (
      <MessengerLayout>
        <div className="p-8">
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-red-300 text-sm">
            Access denied — admin role required.
          </div>
        </div>
      </MessengerLayout>
    );
  }

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Admin Dashboard</h1>
          <p className="text-white/40 text-sm mt-0.5">Organisation-wide overview</p>
        </div>

        {/* Filters + KPI chips */}
        <AdminFilters filters={filters} setFilters={setFilters} kpis={kpis} loading={loading} />

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>
        )}

        {/* Quick access — admin module shortcuts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <button
            onClick={() => navigate("/admin/learning")}
            className="rounded-xl border bg-[#0d0d0d] p-4 text-left transition-colors hover:brightness-110"
            style={{ borderColor: "rgba(127,119,221,0.35)", boxShadow: "0 0 12px rgba(127,119,221,0.10)" }}
          >
            <div className="flex items-center gap-3">
              <span style={{ fontSize: 24 }}>🎓</span>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#7F77DD" }}>
                  Learning &amp; Development
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  Courses, compliance and certifications
                </div>
              </div>
            </div>
          </button>

          {/* Sprint 2 — SSO & Integrations */}
          <button
            onClick={() => navigate("/admin/settings/integrations")}
            className="rounded-xl border bg-[#0d0d0d] p-4 text-left transition-colors hover:brightness-110"
            style={{ borderColor: "rgba(0,255,153,0.35)", boxShadow: "0 0 12px rgba(0,255,153,0.10)" }}
          >
            <div className="flex items-center gap-3">
              <span style={{ fontSize: 24 }}>🔐</span>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#00FF99" }}>
                  SSO &amp; Integrations
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  Google / Microsoft 365 sign-on + directory sync
                </div>
              </div>
            </div>
          </button>

          {/* Sprint 2 — Expenses */}
          <button
            onClick={() => navigate("/admin/expenses")}
            className="rounded-xl border bg-[#0d0d0d] p-4 text-left transition-colors hover:brightness-110"
            style={{ borderColor: "rgba(96,165,250,0.35)", boxShadow: "0 0 12px rgba(96,165,250,0.10)" }}
          >
            <div className="flex items-center gap-3">
              <span style={{ fontSize: 24 }}>🧾</span>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#60a5fa" }}>
                  Expenses
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  Claims approvals, finance review and payroll batching
                </div>
              </div>
            </div>
          </button>

          {/* Sprint 6 — Org Restructure (WF45) */}
          <button
            onClick={() => navigate("/admin/restructure")}
            className="rounded-xl border bg-[#0d0d0d] p-4 text-left transition-colors hover:brightness-110"
            style={{ borderColor: "rgba(251,146,60,0.35)", boxShadow: "0 0 12px rgba(251,146,60,0.10)" }}
          >
            <div className="flex items-center gap-3">
              <span style={{ fontSize: 24 }}>🏢</span>
              <div>
                <div className="text-sm font-semibold" style={{ color: "#fb923c" }}>
                  Org Restructure
                </div>
                <div className="text-xs text-white/40 mt-0.5">
                  Rename, split, merge or move departments with rollback
                </div>
              </div>
            </div>
          </button>

          {/* ER Cases (task 24) — the tenant's own disciplinary and
              grievance caseload, plus the procedures it runs on.

              NO COUNT ON THIS CARD, AND THAT IS A DECISION RATHER THAN AN
              OMISSION. The obvious number to show is "cases past target",
              and it cannot be had honestly from here:

                - GET /api/cases/admin/cases is behind a STEP-UP. An admin
                  who has not completed one would have every dashboard load
                  fire a request that 403s, and the dashboard is not the
                  place to raise an MFA prompt.
                - that read WRITES AN AUDIT ROW, one per call, by design —
                  it discloses case owners' names. Fetching it to render a
                  badge would file "the organisation's case list was read"
                  on every dashboard load by every admin, whether or not
                  anybody looked, and bury the rows that mean something.
                - `total` under breached=true is not the breached count
                  anyway: the breach flag is a function of the working-day
                  calendar and of every pause, so it is applied per case
                  after the page rather than in the query.

              A count therefore needs its own endpoint — counts only, no
              step-up, no audit row — which is a backend change and was
              outside task 24's authority. Until then the card links, and
              the number lives one click away where the disclosure is
              already recorded.

              A lucide icon, not an emoji, unlike its neighbours here. The
              ER module's rule (components/cases/ui.jsx) is that an emoji is
              a font glyph that renders differently on every platform,
              cannot inherit the accent colour and has no accessible name —
              and on a disciplinary surface it carries a tone nobody chose. */}
          {showCases && erModuleOn && (
            <button
              onClick={() => navigate("/admin/cases")}
              className="rounded-xl border bg-[#0d0d0d] p-4 text-left transition-colors hover:brightness-110"
              style={{ borderColor: "rgba(0,255,153,0.35)", boxShadow: "0 0 12px rgba(0,255,153,0.10)" }}
            >
              <div className="flex items-center gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center" style={{ color: "#00FF99" }}>
                  <ScrollText size={22} strokeWidth={1.7} />
                </span>
                <div>
                  <div className="text-sm font-semibold" style={{ color: "#00FF99" }}>
                    ER Cases
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">
                    Organisation caseload oversight and case type settings
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* Demo reset — full server reseed + browser onboarding cleanup */}
          <DemoResetButton variant="card" />
        </div>

        {/* Widget grid — one shared boundary since all 8 widgets share the
            parent's fetched data. A crash inside the grid keeps the header,
            filters, KPI chips, and quick-access buttons rendering. */}
        <ErrorBoundary variant="panel" label="Admin widgets">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <OrgComposition   loading={loading} data={data?.composition} />
            <AttendanceLeave  loading={loading} data={data?.attendance}  />
            <PayrollSummary   loading={loading} data={data?.payroll}     kpis={kpis} />
            <PerformanceRadar loading={loading} data={data?.performance} kpis={kpis} />
            <TicketsStatus    loading={loading} data={data?.tickets}     kpis={kpis} />
            <CompliancePanel  loading={loading} data={data?.compliance}  />
            <ApprovalsHub     loading={loading} data={data?.approvals}   />
            <AnnouncementsCaster />
          </div>
        </ErrorBoundary>

      </div>
    </MessengerLayout>
  );
}